import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Container } from "@/components/ui-blocks";
import Seo from "@/components/Seo";
import { isOrderable, orderCtaLabel } from "@/lib/stock";
import { fetchProductById, fetchPricing } from "@/services/api";
import { useCart } from "@/context/CartContext";
import { StockBadge } from "@/components/ProductCard";
import BulkOffers from "@/components/BulkOffers";
import { cheapestDelivery, leadTimeSentences } from "@/lib/deliveryCopy";
import { priceOrder } from "@/lib/pricingEngine";
import BulkNudge from "@/components/BulkNudge";
import { ChevronRight, Truck, RefreshCw, ShieldCheck } from "lucide-react";
import QuantityInput from "@/components/QuantityInput";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState("");
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchProductById(id).then((p) => {
      if (!p) {
        navigate("/shop", { replace: true });
        return;
      }
      setProduct(p);
      setActiveImg(p.image || (p.gallery && p.gallery[0]) || "");
      setLoading(false);
    });
    fetchPricing().then(setPricing).catch(() => {});
  }, [id, navigate]);

  if (loading || !product) {
    return (
      <div className="py-32 text-center" data-testid="product-detail-loading">
        <div className="font-serif-display text-2xl">Loading…</div>
      </div>
    );
  }

  // Quoted from the rules so the badge cannot outlive a rate change.
  const cheapest = cheapestDelivery(pricing);

  // Priced for the quantity currently selected. The home market is assumed
  // because no address has been given yet; the cart does the same, and the
  // figure shown is only a prompt, never a charge.
  const nudgeQuote = priceOrder(
    [{
      productId: product.id,
      title: product.title,
      price: Number(product.price) || 0,
      qty,
      shippingCost: Number(product.shippingCost) || 0,
    }],
    pricing || {},
    "Singapore",
  );
  const leadTimes = leadTimeSentences(pricing);

  // Primary image first, then any extra shots, with duplicates dropped.
  const images = [
    product.image,
    ...(Array.isArray(product.gallery) ? product.gallery : []),
  ].filter((url, i, all) => url && all.indexOf(url) === i);

  const isTBA = product.priceTBA;
  const orderable = isOrderable(product);
  const ctaDisabled = !orderable;
  const ctaLabel = orderCtaLabel(product);

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    image: product.image ? [product.image] : undefined,
    description: product.shortDescription || product.description || "",
    sku: product.sku || undefined,
    brand: { "@type": "Brand", name: "Ace Asia Productions" },
    ...(isTBA
      ? {}
      : {
          offers: {
            "@type": "Offer",
            priceCurrency: "SGD",
            price: Number(product.price || 0).toFixed(2),
            availability: orderable
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            url: `https://aceasia.com.sg/product/${product.id}`,
          },
        }),
  };

  return (
    <div data-testid="product-detail-page" className="bg-[#FDFBF7]">
      <Seo
        title={product.title}
        description={product.shortDescription || product.description || `${product.title} — Ace Asia Productions`}
        jsonLd={productJsonLd}
      />
      <Container>
        <nav className="py-6 text-xs overline text-[#946f36] flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-[#6e5022]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/shop/${product.category}`} className="hover:text-[#6e5022]">
            {product.category === "books" ? "Books" : "Collectibles"}
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#281a12]">{product.title}</span>
        </nav>
      </Container>

      <Container>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 pb-20">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="bg-[#F4F1EA] aspect-[4/5] overflow-hidden">
              <img
                src={activeImg}
                alt={product.title}
                data-testid="product-detail-main-image"
                className="w-full h-full object-contain p-6"
              />
            </div>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {images.map((g, i) => (
                  <button
                    key={g}
                    onClick={() => setActiveImg(g)}
                    data-testid={`product-thumb-${i}`}
                    className={`aspect-square bg-[#F4F1EA] overflow-hidden border-2 ${
                      activeImg === g ? "border-[#6e5022]" : "border-transparent"
                    }`}
                  >
                    <img src={g} alt="" className="w-full h-full object-contain p-1.5" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <div className="overline text-[#946f36]">
              {product.category === "books" ? "Ace Asia Books" : "Ace Asia Collectibles"}
            </div>
            <h1
              data-testid="product-detail-title"
              className="mt-3 font-serif-display text-4xl md:text-5xl font-light leading-tight"
            >
              {product.title}
            </h1>
            {product.author && (
              <p className="mt-2 font-serif-display italic text-xl text-[#946f36]">
                by {product.author}
              </p>
            )}

            <div className="mt-6 flex items-center gap-4">
              {isTBA ? (
                <span
                  data-testid="product-detail-price"
                  className="font-serif-display text-xl text-[#6e5022]"
                >
                  Prices available at product launch in 2027
                </span>
              ) : (
                <span
                  data-testid="product-detail-price"
                  className="font-serif-display text-3xl text-[#281a12]"
                >
                  SGD {product.price.toFixed(2)}
                </span>
              )}
              <StockBadge status={product.stockStatus} />
            </div>

            <p className="mt-6 text-[#4A3728] leading-relaxed">{product.description}</p>
            {product.longDescription && (
              <p className="mt-4 text-[#4A3728] leading-relaxed">
                {product.longDescription}
              </p>
            )}

            {product.availabilityDate && (
              <div className="mt-6 text-sm text-[#946f36]" data-testid="product-detail-availability">
                Available from{" "}
                {new Date(product.availabilityDate).toLocaleDateString("en-SG", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            )}

            {/* Qty + add */}
            <div className="mt-8 flex items-center gap-4">
              <QuantityInput
                value={qty}
                onChange={setQty}
                size="lg"
                testId="product-qty-value"
              />
              <button
                data-testid="product-add-to-cart"
                disabled={ctaDisabled}
                onClick={() => addItem(product, qty)}
                className={`flex-1 px-7 py-4 text-xs uppercase tracking-[0.2em] transition-colors ${
                  ctaDisabled
                    ? "bg-[#4A3728]/20 text-[#4A3728] cursor-not-allowed"
                    : "bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7]"
                }`}
              >
                {ctaLabel}
              </button>
            </div>

            {/* Prompted against the quantity they have picked, before adding to
                the cart — that is when changing it costs nothing. */}
            <BulkNudge quote={nudgeQuote} testId="product-nudge" className="mt-4" />

            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-[#6e5022]/20 pt-6">
              <Trust
                icon={Truck}
                label="Delivery"
                sub={cheapest ? `From SGD ${cheapest.rate.toFixed(2)}` : "Worldwide"}
              />
              <Trust icon={ShieldCheck} label="Secure" sub="Card payment" />
              <Trust icon={RefreshCw} label="Care" sub="Crafted to last" />
            </div>

            {leadTimes.length > 0 && (
              <ul className="mt-6 text-xs text-[#946f36] space-y-1" data-testid="product-lead-times">
                {leadTimes.map((line) => <li key={line}>{line}</li>)}
              </ul>
            )}

            {product.sku && (
              <div className="mt-8 text-xs text-[#946f36]">
                SKU: <span className="text-[#4A3728]">{product.sku}</span>
              </div>
            )}
          </div>
        </div>

        {/* Shown only when bulk rules exist for this product. */}
        <div className="pb-16">
          <BulkOffers product={product} />
        </div>
      </Container>
    </div>
  );
}

function Trust({ icon: Icon, label, sub }) {
  return (
    <div>
      <Icon className="w-4 h-4 text-[#6e5022]" strokeWidth={1.5} />
      <div className="text-xs uppercase tracking-[0.15em] text-[#281a12] mt-1.5">{label}</div>
      <div className="text-xs text-[#4A3728]">{sub}</div>
    </div>
  );
}
